'use client';

import Button from '@/components/Button';
import Image from '@/components/Image';
import logger from '@/utils/client-logger';
import { SearchOutlined } from '@zeelool/icon';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useState } from 'react';
import { useContactAPI } from '../hooks/useContactAPI';
import { FAQArticle } from '../page';

interface ContactFAQProps {
  defaultFaqList: FAQArticle[];
  isMobile?: boolean;
  className?: string;
}

export default function ContactFAQ({
  defaultFaqList,
  isMobile = false,
  className = '',
}: ContactFAQProps) {
  const t = useTranslations('contact-zeelool.contactFaq');
  const [searchText, setSearchText] = useState('');
  const [articleList, setArticleList] = useState<FAQArticle[]>(defaultFaqList);
  const [searchTimer, setSearchTimer] = useState<NodeJS.Timeout | null>(null);

  // 使用API Hook
  const { loading: faqLoading, error: apiError, searchFAQArticles, clearError } = useContactAPI();

  // 样式常量 - 提高可维护性
  const styles = {
    // 移动端样式
    mobile: {
      container: 'bg-white px-14 py-20',
      title: 'text-lg font-bb text-[#282828] leading-[18px] text-center mb-[18px]',
      searchWrapper: 'relative mb-20 px-0',
      searchContainer: [
        'relative flex items-center pr-4 justify-center h-40',
        'border border-[#f5f5f5] rounded-[80px] bg-[#f5f5f5] overflow-hidden',
        'focus-within:border-[#463ae8] focus-within:bg-white',
      ].join(' '),
      searchInput: [
        'w-full h-full text-xs text-[#282828] placeholder-[#808080]',
        'bg-transparent border-none outline-none pl-20 pr-[60px]',
      ].join(' '),
      searchButton: [
        'w-32 h-32 mr-20 !bg-[#463ae8] text-white rounded-[80px]',
        'flex items-center justify-center',
      ].join(' '),
      errorBox: 'bg-red-50 border border-red-200 rounded-lg p-12 mb-16',
      loadingContainer: 'text-center mb-20',
      loadingSpinner:
        'animate-spin w-16 h-16 border-2 border-[#808080] border-t-transparent rounded-full mr-8',
      articleSection: 'mb-32',
      articleItem: 'py-14 border-b border-[#e2e2e2] last:border-b-0',
      articleButton: [
        'text-left w-full text-sm font-normal text-[#282828]',
        'leading-[17px] hover:text-[#463ae8] transition-colors',
      ].join(' '),
      noResultsContainer: 'flex flex-col items-center justify-center py-48',
      noResultsText: 'text-sm font-normal text-[#282828] leading-[17px] text-center px-40',
    },
    // 桌面端样式
    desktop: {
      container: [
        'relative h-full w-full right-0 py-32 px-0',
        'rounded-[10px] bg-[#f8f9fa] flex flex-col',
      ].join(' '),
      title: 'text-2xl font-bb font-bb text-[#282828] leading-24 pl-32 mb-24',
      searchWrapper: 'relative mt-24 mb-20 px-32',
      searchContainer: [
        'relative h-[50px] border border-[#dcdfe6] rounded-lg',
        'bg-white overflow-hidden focus-within:border-[#463ae8]',
      ].join(' '),
      searchInput: [
        'w-full h-[50px] text-base text-[#282828] placeholder-[#808080]',
        'bg-transparent border-none outline-none pl-32',
      ].join(' '),
      searchIcon: 'absolute left-12 top-1/2 transform -translate-y-1/2 w-16 h-16 text-[#282828]',
      dropdown: [
        'absolute top-[50px] bg-white w-full',
        'shadow-[0px_3px_10px_0px_rgba(204,204,204,0.35)] p-0 z-[100]',
        'rounded-lg border max-h-[1200px] overflow-y-auto',
      ].join(' '),
      dropdownButton: [
        'block w-full text-left p-16 text-base font-normal text-[#282828]',
        'leading-6 hover:text-[#463ae8] hover:underline hover:bg-[#f8f9fa] transition-colors',
      ].join(' '),
      listContainer: 'relative flex-1 h-0 overflow-hidden box-border',
      listWrapper: 'h-full overflow-y-auto px-32',
      listItem: 'py-16 border-b border-[#dce0e8] last:border-b-0',
      listButton: 'text-left w-full group cursor-pointer',
      listTitle: [
        'inline text-base font-normal leading-4 text-[#282828]',
        'group-hover:text-[#463ae8] group-hover:underline transition-colors',
      ].join(' '),
    },
    // 通用样式
    common: {
      errorText: 'text-sm text-red-600',
      loadingText: 'inline-flex items-center text-sm text-[#808080]',
      noDataImage: 'w-[284px] h-[138px] mx-auto mb-24',
      noDataText: 'block text-lg font-normal text-[#282828] leading-[26px]',
    },
  };

  // 关键词加粗处理
  const addBoldToKeywords = useCallback((text: string, keyword: string): string => {
    if (!text || !keyword) return text;
    const regex = new RegExp(`(${keyword})`, 'gi');
    return text.replace(regex, '<strong>$1</strong>');
  }, []);

  // 搜索FAQ文章
  const searchFaq = useCallback(
    async (query: string) => {
      if (!query.trim()) {
        setArticleList(defaultFaqList);
        return;
      }

      try {
        const results = await searchFAQArticles(query);

        // 将搜索结果转换为FAQ格式，并添加关键词高亮
        const highlightedResults = results.map((item) => ({
          title: addBoldToKeywords(item.title, query),
          link: item.link,
        }));

        setArticleList(highlightedResults);
      } catch (error) {
        logger.error('搜索FAQ失败:', error);
        setArticleList([]);
      }
    },
    [searchFAQArticles, defaultFaqList, addBoldToKeywords],
  );

  // 实时搜索处理
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchText(value);
      clearError(); // 清除之前的错误

      // 清除之前的定时器
      if (searchTimer) {
        clearTimeout(searchTimer);
      }

      if (value.trim()) {
        // 设置新的定时器
        const newTimer = setTimeout(() => {
          searchFaq(value);
        }, 300); // 300ms防抖
        setSearchTimer(newTimer);
      } else {
        setArticleList(defaultFaqList);
      }
    },
    [searchTimer, searchFaq, defaultFaqList, clearError],
  );

  // 处理搜索框回车
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      searchFaq(searchText);
    }
  };

  // 跳转到文章
  const jumpToArticle = (link: string) => {
    window.open(link, '_blank');
  };

  // 清理定时器
  useEffect(() => {
    return () => {
      if (searchTimer) {
        clearTimeout(searchTimer);
      }
    };
  }, [searchTimer]);

  // 渲染加载状态
  const renderLoadingState = () => {
    const containerClass = isMobile ? styles.mobile.loadingContainer : 'p-16 text-center';

    return (
      <div className={containerClass}>
        <div className={styles.common.loadingText}>
          <div className={styles.mobile.loadingSpinner} />
          {t('loading')}
        </div>
      </div>
    );
  };

  // 渲染错误状态
  const renderErrorState = () => {
    const containerClass = isMobile ? styles.mobile.errorBox : 'p-16 text-center text-red-600';

    return (
      <div className={containerClass}>
        <p className={styles.common.errorText}>{apiError}</p>
      </div>
    );
  };

  // 渲染无结果状态
  const renderNoResultsState = () => {
    const containerClass = isMobile ? styles.mobile.noResultsContainer : 'p-32 text-center';

    return (
      <div className={containerClass}>
        <Image
          src="/contact-zeelool/no-data.png"
          alt={t('noResultsAlt')}
          width={284}
          height={138}
          className={styles.common.noDataImage}
        />
        <span
          className={styles.common.noDataText}
          dangerouslySetInnerHTML={{ __html: t('noResultsMessage') }}
        />
      </div>
    );
  };

  // 渲染文章列表项
  const renderArticleItem = (
    item: FAQArticle,
    index: number,
    itemStyle: string,
    buttonStyle: string,
  ) => (
    <div key={index} className={itemStyle}>
      <button
        onClick={() => jumpToArticle(item.link)}
        className={buttonStyle}
        dangerouslySetInnerHTML={{ __html: item.title }}
      />
    </div>
  );

  // 渲染桌面端下拉搜索结果
  const renderDesktopDropdownContent = () => {
    if (apiError) {
      return renderErrorState();
    }

    if (faqLoading) {
      return renderLoadingState();
    }

    if (articleList.length > 0) {
      return articleList
        .slice(0, 5)
        .map((item, index) => (
          <button
            key={index}
            onClick={() => jumpToArticle(item.link)}
            className={styles.desktop.dropdownButton}
            dangerouslySetInnerHTML={{ __html: item.title }}
          />
        ));
    }

    return renderNoResultsState();
  };

  // 渲染移动端搜索按钮内容
  const renderMobileSearchButtonContent = () => {
    if (faqLoading) {
      return null;
    }

    return <SearchOutlined className="z-999 text-white" />;
  };

  if (isMobile) {
    // 移动端布局
    return (
      <div className={`${styles.mobile.container} ${className}`}>
        {/* FAQ标题 */}
        <h2 className={styles.mobile.title}>{t('title')}</h2>

        {/* 搜索框 */}
        <div className={styles.mobile.searchWrapper}>
          <div className={styles.mobile.searchContainer}>
            <input
              type="text"
              value={searchText}
              onChange={(e) => handleSearchChange(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('searchPlaceholder')}
              className={styles.mobile.searchInput}
            />
            <Button
              onClick={() => searchFaq(searchText)}
              loading={faqLoading}
              className={styles.mobile.searchButton}
            >
              {renderMobileSearchButtonContent()}
            </Button>
          </div>
        </div>

        {/* 错误信息显示 */}
        {apiError && renderErrorState()}

        {/* 加载状态 */}
        {faqLoading && renderLoadingState()}

        {/* FAQ结果列表 */}
        {!faqLoading && articleList.length > 0 && (
          <section className={styles.mobile.articleSection}>
            {articleList.map((item, index) =>
              renderArticleItem(
                item,
                index,
                styles.mobile.articleItem,
                styles.mobile.articleButton,
              ),
            )}
          </section>
        )}

        {/* 无结果状态 */}
        {!faqLoading && searchText && articleList.length === 0 && renderNoResultsState()}
      </div>
    );
  }

  // 桌面端布局
  return (
    <div className={`${styles.desktop.container} ${className}`}>
      {/* FAQ标题 */}
      <h2 className={styles.desktop.title}>{t('title')}</h2>

      {/* 搜索框 */}
      <div className={styles.desktop.searchWrapper}>
        <div className="relative">
          <div className={styles.desktop.searchContainer}>
            <input
              type="text"
              value={searchText}
              onChange={(e) => handleSearchChange(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('searchPlaceholder')}
              className={styles.desktop.searchInput}
            />
            <div className={styles.desktop.searchIcon}>
              <SearchOutlined />
            </div>
          </div>

          {/* 搜索下拉结果 */}
          {searchText && (
            <div className={styles.desktop.dropdown}>{renderDesktopDropdownContent()}</div>
          )}
        </div>
      </div>

      {/* FAQ结果列表 */}
      <div className={styles.desktop.listContainer}>
        {!faqLoading && !searchText && articleList.length > 0 && (
          <div className={styles.desktop.listWrapper}>
            <ul className="pb-16">
              {articleList.map((item, index) => (
                <li key={index} className={styles.desktop.listItem}>
                  <button
                    onClick={() => jumpToArticle(item.link)}
                    className={styles.desktop.listButton}
                  >
                    <h4
                      className={styles.desktop.listTitle}
                      dangerouslySetInnerHTML={{ __html: item.title }}
                    />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* 无结果状态 - 桌面端 */}
        {!faqLoading && !searchText && articleList.length === 0 && (
          <div className="py-48 text-center">{renderNoResultsState()}</div>
        )}
      </div>
    </div>
  );
}
